import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Toast } from 'primeng/toast';

import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import {
  TableColumn,
  TableComponent,
} from '@app/shared/components/table/table.component';
import { Pagination } from '@app/shared/model';

import {
  DocumentoItem,
  FiltroDocumentos,
  OpcaoVaga,
  TipoPublico,
} from '../../api/models/documento.model';
import { RelatorioCandidaturasFiltro } from '../../api/models/relatorio-candidaturas-filtro.model';
import { DocumentosService } from '../../api/services/documentos.service';

import { ConcursoDrhService } from '../../../gestao-de-concurso/core/concurso-drh.service';
import { OfertasConcursoService } from '../../../gestao-de-concurso/core/ofertas-concurso.service';
import { ConcursoDTO, OfertaDto } from '../../../gestao-de-concurso/core/concurso-drh.models';

@Component({
  selector: 'app-report-documentos',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    Dialog,
    SelectModule,
    DatePickerModule,
    InputTextModule,
    Toast,
    TagModule,
    TableModule,
    TableComponent,
    DataVoidComponent,
  ],
  templateUrl: './report-documentos.component.html',
  styleUrls: ['./report-documentos.component.scss'],
})
export class ReportDocumentosComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly documentosService = inject(DocumentosService);
  private readonly concursoService = inject(ConcursoDrhService);
  private readonly ofertasService = inject(OfertasConcursoService);

  formFiltros!: FormGroup;
  documentos: DocumentoItem[] = [];
  todosDocumentosOriginais: DocumentoItem[] = [];
  pagination = new Pagination<DocumentoItem>({
    pageNumber: 1,
    pageSize: 5,
    totalRecords: 0,
    totalPages: 1,
    data: [],
  });
  totalDocumentos = 0;
  carregando = false;
  showModalFiltros = false;
  exportandoPdf = false;
  exportandoWord = false;

  appliedFilters: { label: string; value: string }[] = [];
  vagasList: { label: string; value: string }[] = [];
  concursosList: { label: string; value: string }[] = [];
  ofertasList: { label: string; value: string }[] = [];
  aCarregarOfertas = false;

  readonly titlleOptions = [
    {
      label: 'Relatório Geral de Candidaturas e Documentos',
      value: 'Relatório Geral de Candidaturas e Documentos',
    },
    {
      label: 'Relatório de Candidaturas por Oferta de Concurso',
      value: 'Relatório de Candidaturas por Oferta de Concurso',
    },
    {
      label: 'Relatório de Documentos de Profissionais',
      value: 'Relatório de Documentos de Profissionais',
    },
    {
      label: 'Relatório de Documentos de Estudantes / Bolseiros',
      value: 'Relatório de Documentos de Estudantes / Bolseiros',
    },
    {
      label: 'Relatório de Comprovativos e Fichas Emitidas',
      value: 'Relatório de Comprovativos e Fichas Emitidas',
    },
  ];

  readonly tipoPublicoOptions: { label: string; value: TipoPublico }[] = [
    { label: 'Todos os Públicos', value: 'TODOS' },
    { label: 'Profissionais', value: 'PROFISSIONAL' },
    { label: 'Estudantes / Bolseiros', value: 'ESTUDANTE' },
  ];

  readonly formatoOptions = [
    { label: 'Todos os Formatos', value: 'TODOS' },
    { label: 'Apenas PDF (.pdf)', value: 'PDF' },
    { label: 'Documentos Word (.docx)', value: 'DOCX' },
    { label: 'Planilhas Excel (.xlsx)', value: 'XLSX' },
  ];

  columns: TableColumn[] = [
    {
      header: 'Título do Relatório',
      field: 'titulo',
      type: 'text',
      styleClass: 'font-semibold text-primary',
    },
    {
      header: 'Tipo',
      field: 'tipoPublico',
      type: 'tag',
      formatter: () => 'Relatório Oficial',
      tagSeverityFunction: () => 'info',
    },
    {
      header: 'Âmbito / Concurso',
      valueGetter: (row: DocumentoItem) =>
        row.concursoNome || (row.concursoId ? `Concurso #${row.concursoId}` : 'Geral (Todos os Concursos)'),
      type: 'text',
    },
    {
      header: 'Formato',
      field: 'formato',
      type: 'tag',
      formatter: (val: string) => (val ? val.toUpperCase() : 'PDF'),
      tagSeverityFunction: (row: DocumentoItem) => {
        const f = (row.formato || '').toLowerCase();
        if (f.includes('pdf')) return 'danger';
        if (f.includes('doc')) return 'primary';
        return 'secondary';
      },
    },
    {
      header: 'Data de Referência',
      field: 'dataCriacao',
      type: 'date',
      dateFormat: 'dd/MM/yyyy HH:mm',
    },
  ];

  actions = (doc: DocumentoItem): MenuItem[] => [
    {
      label: doc.formato?.toUpperCase() === 'PDF' ? 'Descarregar PDF' : 'Descarregar Word',
      icon: doc.formato?.toUpperCase() === 'PDF' ? 'pi pi-file-pdf' : 'pi pi-file-word',
      command: () => this.descarregar(doc),
    },
    {
      label: 'Copiar Título',
      icon: 'pi pi-copy',
      command: () => this.copiarId(doc),
    },
  ];

  ngOnInit(): void {
    this.iniciarFormulario();
    this.carregarVagas();
    this.carregarConcursos();
    this.carregarDocumentos();
    this.atualizarFiltrosAplicados();
    this.showModalFiltros = false;
  }

  private iniciarFormulario(): void {
    this.formFiltros = this.fb.group({
      titulo: ['Relatório Geral de Candidaturas e Documentos', Validators.required],
      pesquisa: [''],
      tipoPublico: ['TODOS'],
      formato: ['TODOS'],
      vagaId: [null],
      concursoId: [null],
      ofertaId: [null],
      dataInicio: [null],
      dataFim: [null],
    });

    // Ao alterar o concurso, atualiza as ofertas disponíveis
    this.formFiltros.get('concursoId')?.valueChanges.subscribe((concursoId) => {
      this.ofertasList = [];
      this.formFiltros.get('ofertaId')?.setValue(null);
      if (concursoId) {
        this.carregarOfertasDoConcurso(concursoId);
      }
    });
  }

  carregarConcursos(): void {
    this.concursoService.listarConcursos(1, 100).subscribe({
      next: ({ data }) => {
        const concursos: ConcursoDTO[] = data ?? [];
        this.concursosList = concursos.map((c) => ({
          label: `${c.codigo ? c.codigo + ' — ' : ''}${c.nome || c.designacao || 'Concurso'}`,
          value: String(c.id),
        }));
      },
      error: () => {
        this.concursosList = [];
      },
    });
  }

  carregarOfertasDoConcurso(concursoId: string): void {
    this.aCarregarOfertas = true;
    this.ofertasService.doConcurso({ id: concursoId }).subscribe({
      next: (res) => {
        this.aCarregarOfertas = false;
        const ofertas: OfertaDto[] = res.ofertas ?? [];
        this.ofertasList = ofertas.map((o) => ({
          label: `${this.ofertasService.rotulo(o)} · ${o.vagasTotais} vagas`,
          value: String(o.id),
        }));
      },
      error: () => {
        this.aCarregarOfertas = false;
        this.ofertasList = [];
      },
    });
  }

  carregarVagas(): void {
    this.documentosService.listarVagas().subscribe({
      next: (vagas: any) => {
        const lista: any[] = Array.isArray(vagas)
          ? vagas
          : (vagas?.data ?? []);
        this.vagasList = lista.map((v: any) => ({
          label: v.designacaoFuncao
            ? `${v.designacaoFuncao}${v.empresa ? ' · ' + v.empresa : ''}`
            : (v.titulo || v.nome || `Vaga #${v.id}`),
          value: String(v.id),
        }));
      },
      error: (err) => {
        console.error('Erro ao carregar vagas da API:', err);
        this.vagasList = [];
      },
    });
  }

  carregarDocumentos(): void {
    this.carregando = true;
    const filtro = this.construirFiltroDocumentos();

    // Obtém o nome legível do Concurso e Oferta caso estejam selecionados no formulário
    let concursoDescricao = 'Geral (Todos os Concursos)';
    if (filtro.concursoId) {
      const conc = this.concursosList.find((c) => String(c.value) === String(filtro.concursoId));
      if (conc) {
        concursoDescricao = conc.label;
      }
    }

    if (filtro.ofertaId && this.ofertasList.length > 0) {
      const oferta = this.ofertasList.find((o) => String(o.value) === String(filtro.ofertaId));
      if (oferta) {
        concursoDescricao += ` · ${oferta.label}`;
      }
    }

    const agora = new Date().toISOString();

    const relatoriosOficiais: DocumentoItem[] = [
      {
        id: 'relatorio-candidaturas-pdf',
        titulo: 'Relatório Oficial de Candidaturas Submetidas',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'PDF',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-candidaturas-word',
        titulo: 'Relatório Oficial de Candidaturas Submetidas (Editável)',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'DOCX',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-candidaturas-vagas-pdf',
        titulo: 'Relatório de Candidaturas a Vagas do Sector',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'PDF',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-candidaturas-vagas-word',
        titulo: 'Relatório de Candidaturas a Vagas do Sector (Editável)',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'DOCX',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-profissionais-pdf',
        titulo: 'Relatório Geral de Profissionais Inscritos',
        tipoPublico: 'PROFISSIONAL',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'PDF',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-profissionais-word',
        titulo: 'Relatório Geral de Profissionais Inscritos (Editável)',
        tipoPublico: 'PROFISSIONAL',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'DOCX',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-estudantes-pdf',
        titulo: 'Relatório de Estudantes e Bolseiros Activos',
        tipoPublico: 'ESTUDANTE',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'PDF',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-estudantes-word',
        titulo: 'Relatório de Estudantes e Bolseiros Activos (Editável)',
        tipoPublico: 'ESTUDANTE',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'DOCX',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-vagas-pdf',
        titulo: 'Relatório de Vagas e Oportunidades do Sector',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'PDF',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-vagas-word',
        titulo: 'Relatório de Vagas e Oportunidades do Sector (Editável)',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'DOCX',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-comprovativos-pdf',
        titulo: 'Relatório de Comprovativos e Fichas Emitidas',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'PDF',
        dataCriacao: agora,
      },
      {
        id: 'relatorio-comprovativos-word',
        titulo: 'Relatório de Comprovativos e Fichas Emitidas (Editável)',
        tipoPublico: 'TODOS',
        concursoId: filtro.concursoId,
        concursoNome: concursoDescricao,
        ofertaId: filtro.ofertaId,
        formato: 'DOCX',
        dataCriacao: agora,
      },
    ];

    let filtrados = relatoriosOficiais;

    if (filtro.tipoPublico && filtro.tipoPublico !== 'TODOS') {
      filtrados = filtrados.filter(
        (r) => r.tipoPublico === filtro.tipoPublico || r.tipoPublico === 'TODOS'
      );
    }

    if (filtro.titulo && filtro.titulo !== 'Relatório Geral de Candidaturas e Documentos') {
      const termo = filtro.titulo.toLowerCase();
      filtrados = filtrados.filter((r) => {
        const tit = r.titulo.toLowerCase();
        if (termo.includes('candidatura')) return tit.includes('candidatura');
        if (termo.includes('profissional')) return tit.includes('profissional');
        if (termo.includes('estudante') || termo.includes('bolseiro')) return tit.includes('estudante') || tit.includes('bolseiro');
        if (termo.includes('vaga')) return tit.includes('vaga');
        if (termo.includes('comprovativo') || termo.includes('ficha')) return tit.includes('comprovativo') || tit.includes('ficha');
        return tit.includes(termo.replace('relatório de ', '').replace('relatório geral de ', '').trim());
      });
    }

    if (filtro.formato && filtro.formato !== 'TODOS') {
      filtrados = filtrados.filter(
        (r) => r.formato.toUpperCase() === filtro.formato?.toUpperCase()
      );
    }

    if (filtro.pesquisa && filtro.pesquisa.trim()) {
      const termo = filtro.pesquisa.trim().toLowerCase();
      filtrados = filtrados.filter(
        (r) =>
          r.titulo.toLowerCase().includes(termo) ||
          (r.concursoNome && r.concursoNome.toLowerCase().includes(termo))
      );
    }

    this.todosDocumentosOriginais = filtrados;
    this.documentos = filtrados;
    this.totalDocumentos = filtrados.length;
    this.atualizarPaginacao();
    this.carregando = false;
  }

  aplicarFiltros(): void {
    this.formFiltros.markAllAsTouched();
    if (this.formFiltros.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Por favor, preencha os campos obrigatórios do formulário.',
        life: 3000,
      });
      return;
    }

    this.pagination.pageNumber = 1;
    this.carregarDocumentos();
    this.atualizarFiltrosAplicados();
    this.showModalFiltros = false;

    this.messageService.add({
      severity: 'success',
      summary: 'Relatório Emitido',
      detail: `Filtros aplicados e relatório atualizado com sucesso.`,
      life: 3000,
    });
  }

  limparFiltros(): void {
    this.formFiltros.reset({
      titulo: 'Relatório Geral de Candidaturas e Documentos',
      pesquisa: '',
      tipoPublico: 'TODOS',
      formato: 'TODOS',
      vagaId: null,
      concursoId: null,
      ofertaId: null,
      dataInicio: null,
      dataFim: null,
    });
    this.appliedFilters = [];
    this.pagination.pageNumber = 1;
    this.carregarDocumentos();
    this.showModalFiltros = false;

    this.messageService.add({
      severity: 'info',
      summary: 'Filtros Reiniciados',
      detail: 'Parâmetros de pesquisa restaurados para o padrão.',
      life: 2500,
    });
  }

  onPageChange(event: any): void {
    const page =
      event.page !== undefined
        ? event.page + 1
        : Math.floor((event.first ?? 0) / (event.rows ?? this.pagination.pageSize ?? 5)) + 1;
    this.pagination.pageNumber = page;
    this.pagination.pageSize = event.rows ?? this.pagination.pageSize ?? 5;
    this.atualizarPaginacao();
  }

  private atualizarPaginacao(): void {
    const total = this.documentos.length;
    const size = this.pagination.pageSize || 5;
    const totalPages = Math.max(1, Math.ceil(total / size));
    const page = Math.min(Math.max(1, this.pagination.pageNumber || 1), totalPages);
    const inicio = (page - 1) * size;
    const fim = inicio + size;
    const dadosPaginados = this.documentos.slice(inicio, fim);

    this.pagination = new Pagination<DocumentoItem>({
      pageNumber: page,
      pageSize: size,
      totalRecords: total,
      records: dadosPaginados.length,
      totalPages: totalPages,
      data: dadosPaginados,
    });
    this.totalDocumentos = total;
  }

  private construirFiltroDocumentos(): FiltroDocumentos {
    const form = this.formFiltros.value;
    const filtro: FiltroDocumentos = {
      pageNumber: this.pagination.pageNumber || 1,
      pageSize: this.pagination.pageSize || 5,
    };

    if (form.titulo) {
      filtro.titulo = form.titulo;
    }
    if (form.tipoPublico && form.tipoPublico !== 'TODOS') {
      filtro.tipoPublico = form.tipoPublico;
    }
    if (form.vagaId) {
      filtro.vagaId = form.vagaId;
    }
    if (form.concursoId) {
      filtro.concursoId = form.concursoId;
    }
    if (form.ofertaId) {
      filtro.ofertaId = form.ofertaId;
    }
    if (form.formato && form.formato !== 'TODOS') {
      filtro.formato = form.formato;
    }
    if (form.pesquisa?.trim()) {
      filtro.pesquisa = form.pesquisa.trim();
    }
    if (form.dataInicio) {
      filtro.dataInicio = form.dataInicio;
    }
    if (form.dataFim) {
      filtro.dataFim = form.dataFim;
    }

    return filtro;
  }

  private construirFiltroCandidaturas(): RelatorioCandidaturasFiltro {
    const form = this.formFiltros.value;
    const filtro: RelatorioCandidaturasFiltro = {};

    if (form.dataInicio) filtro.dataInicio = form.dataInicio;
    if (form.dataFim) filtro.dataFim = form.dataFim;
    if (form.concursoId) filtro.concursoId = form.concursoId;
    if (form.ofertaId) filtro.ofertaId = form.ofertaId;

    return filtro;
  }

  private filtrarListaLocalmente(dadosOriginais: DocumentoItem[]): DocumentoItem[] {
    const form = this.formFiltros?.value || {};
    let dados = [...dadosOriginais];

    if (form.tipoPublico && form.tipoPublico !== 'TODOS') {
      dados = dados.filter((d) => d.tipoPublico === form.tipoPublico);
    }
    if (form.vagaId) {
      dados = dados.filter((d) => d.vagaId === String(form.vagaId));
    }
    if (form.concursoId) {
      dados = dados.filter((d) => !d.concursoId || d.concursoId === String(form.concursoId));
    }
    if (form.ofertaId) {
      dados = dados.filter((d) => !d.ofertaId || d.ofertaId === String(form.ofertaId));
    }
    if (form.formato && form.formato !== 'TODOS') {
      dados = dados.filter((d) =>
        (d.formato || '').toUpperCase().includes(form.formato),
      );
    }
    if (form.pesquisa?.trim()) {
      const q = form.pesquisa.toLowerCase().trim();
      dados = dados.filter(
        (d) =>
          d.titulo.toLowerCase().includes(q) ||
          (d.vagaNome && d.vagaNome.toLowerCase().includes(q)),
      );
    }
    if (form.dataInicio) {
      const inicio = new Date(form.dataInicio).getTime();
      dados = dados.filter((d) => new Date(d.dataCriacao).getTime() >= inicio);
    }
    if (form.dataFim) {
      const fim = new Date(form.dataFim).getTime() + 86400000;
      dados = dados.filter((d) => new Date(d.dataCriacao).getTime() <= fim);
    }

    return dados;
  }

  private atualizarFiltrosAplicados(): void {
    const form = this.formFiltros.value;
    const filtros: { label: string; value: string }[] = [];

    if (form.titulo) {
      filtros.push({ label: 'Tipo de Relatório', value: form.titulo });
    }
    if (form.pesquisa?.trim()) {
      filtros.push({ label: 'Termo', value: `"${form.pesquisa.trim()}"` });
    }
    if (form.tipoPublico && form.tipoPublico !== 'TODOS') {
      const publico = this.tipoPublicoOptions.find(
        (t) => t.value === form.tipoPublico,
      )?.label;
      filtros.push({
        label: 'Público-Alvo',
        value: publico || form.tipoPublico,
      });
    }
    if (form.formato && form.formato !== 'TODOS') {
      filtros.push({ label: 'Formato', value: form.formato });
    }
    if (form.concursoId) {
      const concurso = this.concursosList.find(
        (c) => c.value === form.concursoId,
      )?.label;
      filtros.push({ label: 'Concurso', value: concurso || form.concursoId });
    }
    if (form.ofertaId) {
      const oferta = this.ofertasList.find((o) => o.value === form.ofertaId)?.label;
      filtros.push({ label: 'Oferta', value: oferta || form.ofertaId });
    }
    if (form.vagaId) {
      const vaga = this.vagasList.find((v) => v.value === form.vagaId)?.label;
      filtros.push({ label: 'Vaga', value: vaga || `Vaga #${form.vagaId}` });
    }
    if (form.dataInicio) {
      const d = new Date(form.dataInicio).toLocaleDateString('pt-AO');
      filtros.push({ label: 'Início', value: d });
    }
    if (form.dataFim) {
      const d = new Date(form.dataFim).toLocaleDateString('pt-AO');
      filtros.push({ label: 'Fim', value: d });
    }

    this.appliedFilters = filtros;
  }

  downloadPdf(): void {
    this.exportandoPdf = true;
    const filtro = this.construirFiltroCandidaturas();

    this.documentosService.obterRelatorioCandidaturasPdf(filtro).subscribe({
      next: (blob: Blob) => {
        this.exportandoPdf = false;
        this.fazerDownloadBlob(blob, 'Relatorio-Candidaturas.pdf');
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Relatório de candidaturas em PDF exportado com sucesso.',
        });
      },
      error: () => {
        this.exportandoPdf = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Falha na Exportação',
          detail: 'Não foi possível descarregar o relatório em PDF.',
        });
      },
    });
  }

  downloadWord(): void {
    this.exportandoWord = true;
    const filtro = this.construirFiltroCandidaturas();

    this.documentosService.obterRelatorioCandidaturasWord(filtro).subscribe({
      next: (blob: Blob) => {
        this.exportandoWord = false;
        this.fazerDownloadBlob(blob, 'Relatorio-Candidaturas.docx');
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Relatório de candidaturas em Word exportado com sucesso.',
        });
      },
      error: () => {
        this.exportandoWord = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Falha na Exportação',
          detail: 'Não foi possível descarregar o relatório em Word.',
        });
      },
    });
  }

  descarregar(doc: DocumentoItem): void {
    if (doc.formato?.toUpperCase() === 'PDF') {
      this.downloadPdf();
    } else if (doc.formato?.toUpperCase() === 'DOCX') {
      this.downloadWord();
    } else if (doc.urlDownload) {
      window.open(doc.urlDownload, '_blank');
    } else {
      this.downloadPdf();
    }
  }

  copiarId(doc: DocumentoItem): void {
    if (doc.id) {
      navigator.clipboard?.writeText(doc.id);
      this.messageService.add({
        severity: 'info',
        summary: 'Copiado',
        detail: `Identificador copiado: ${doc.id}`,
      });
    }
  }

  formatarTamanho(kb?: number): string {
    if (!kb && kb !== 0) return '—';
    if (kb < 1024) return `${kb} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  private fazerDownloadBlob(blob: Blob, nomeFicheiro: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomeFicheiro;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
